import type { MutableRefObject } from 'react';
import { Controller, type Control } from 'react-hook-form';
import { Alert, Input, Label } from '@/shared/components';
import type { DraftFormValues } from '../schemas/documentTemplate.schema';
import { useUnknownPlaceholders } from '../hooks/useUnknownPlaceholders';
import { TemplateBodyField } from './BodyEditor/TemplateBodyField';

interface Props {
  control: Control<DraftFormValues>;
  bodyHtml: string;
  disabled?: boolean;
  insertRef?: MutableRefObject<((token: string) => void) | null>;
}

export function TemplateBodyFields({ control, bodyHtml, disabled = false, insertRef }: Props) {
  const unknownKeys = useUnknownPlaceholders(bodyHtml) ?? [];

  return (
    <>
      <TemplateBodyField control={control} disabled={disabled} insertRef={insertRef} />

      {unknownKeys.length > 0 && (
        <Alert variant="warning" role="status">
          <span>
            Not in the variable catalogue: {unknownKeys.map((key) => `{{${key}}}`).join(', ')}. The server checks
            this when you save.
          </span>
        </Alert>
      )}

      <div>
        <Label htmlFor="draft-change-note">Change note</Label>
        <Controller
          name="changeNote"
          control={control}
          render={({ field, fieldState: { error } }) => (
            <Input
              id="draft-change-note"
              value={field.value}
              onChange={field.onChange}
              onBlur={field.onBlur}
              disabled={disabled}
              placeholder="Optional"
              error={!!error}
              hint={error?.message ?? 'Shown in version history when this draft is published.'}
              hintId="draft-change-note-hint"
              aria-invalid={!!error}
              aria-describedby="draft-change-note-hint"
            />
          )}
        />
      </div>
    </>
  );
}
